import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { constant, set } from 'lodash/fp';
import { combineReducers } from 'redux';

import { ReservationPriceResult } from '@mews-distributor/data-access';
import { createReducer } from '@mews/framework/redux';

import { Metadata } from '../../../types/Metadata';
import { BOOKING_CLEARED, BOOKING_RESTARTED } from '../../global/actionTypes';
import {
    FETCH_RESERVATIONS_PRICE_FAILED,
    FETCH_RESERVATIONS_PRICE_STARTED,
    FETCH_RESERVATIONS_PRICE_SUCCEEDED,
} from './actionTypes';
import { ReservationPricesData, ReservationPricesMetaData, ReservationTotalAmountGatewayCurrencyData } from './types';

const dataInitialState: ReservationPricesData = {};
const reservationsTotalAmountInPgaCurrencyState: ReservationTotalAmountGatewayCurrencyData = {};
const metaInitialState: ReservationPricesMetaData = {};

const setInitialDataState = constant(dataInitialState);
const setInitialTotalAmountPgaCurrencyState = constant(reservationsTotalAmountInPgaCurrencyState);

export interface ReservationPricePayload extends ReservationPriceResult {
    reservationGroupId: string;
}
const enhanceReservationPriceWithCancellationFees = (
    reservationPrices: ReservationPriceResult['reservationPrice'],
    reservationCancellationFees: ReservationPriceResult['cancellationFees']
) =>
    pipe(
        reservationPrices,
        array.map((reservationPrice) => {
            const cancellationFees = pipe(
                reservationCancellationFees,
                array.filterMap((reservationCancellationFee) =>
                    reservationCancellationFee.identifier === reservationPrice.identifier
                        ? option.some(reservationCancellationFee.fees)
                        : option.none
                ),
                array.flatten
            );
            return { ...reservationPrice, cancellationFees };
        })
    );

const dataReducer = createReducer(dataInitialState, {
    [FETCH_RESERVATIONS_PRICE_SUCCEEDED]: (
        state: ReservationPricesData,
        { reservationPrice, reservationGroupId, cancellationFees }: ReservationPricePayload
    ) =>
        set(reservationGroupId, enhanceReservationPriceWithCancellationFees(reservationPrice, cancellationFees), state),
    [FETCH_RESERVATIONS_PRICE_STARTED]: setInitialDataState,
    [BOOKING_CLEARED]: setInitialDataState,
    [BOOKING_RESTARTED]: setInitialDataState,
});

const totalAmountPaymentGatewayCurrencyReducer = createReducer(reservationsTotalAmountInPgaCurrencyState, {
    [FETCH_RESERVATIONS_PRICE_SUCCEEDED]: (
        state: ReservationTotalAmountGatewayCurrencyData,
        { reservationGroupId, totalAmountGatewayCurrency }: ReservationPricePayload
    ) => set(reservationGroupId, totalAmountGatewayCurrency, state),
    [FETCH_RESERVATIONS_PRICE_STARTED]: setInitialTotalAmountPgaCurrencyState,
    [BOOKING_CLEARED]: setInitialTotalAmountPgaCurrencyState,
    [BOOKING_RESTARTED]: setInitialTotalAmountPgaCurrencyState,
});

const updateMeta =
    ({ hasFailed, isFetching }: Metadata) =>
    (state: ReservationPricesMetaData, { reservationGroupId }: ReservationPricePayload) =>
        set(
            reservationGroupId,
            {
                hasFailed,
                isFetching,
            },
            state
        );

const metaReducer = createReducer(metaInitialState, {
    [FETCH_RESERVATIONS_PRICE_STARTED]: updateMeta({ hasFailed: false, isFetching: true }),
    [FETCH_RESERVATIONS_PRICE_SUCCEEDED]: updateMeta({ hasFailed: false, isFetching: false }),
    [FETCH_RESERVATIONS_PRICE_FAILED]: updateMeta({ hasFailed: true, isFetching: false }),
});

export const reservationPricesReducer = combineReducers({
    data: dataReducer,
    reservationsTotalAmountInPgaCurrency: totalAmountPaymentGatewayCurrencyReducer,
    meta: metaReducer,
});
